import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

export const EMAIL_DOMAIN_URL = '/email_domain';
export const EMAIL_DOMAIN_QUERY_KEY = ['emailDomain'];

const DnsRecordSchema = z.object({
  type: z.string(),
  host: z.string(),
  data: z.string(),
  valid: z.boolean(),
});

export type DnsRecord = z.infer<typeof DnsRecordSchema>;

const EmailDomainSchema = z.object({
  // Null when no sending domain has been set up; the other two fields are still
  // present, so callers never have to branch on the response shape.
  domain: z.string().nullable(),
  domainValid: z.boolean(),
  dnsRecords: z.array(DnsRecordSchema),
});

export type EmailDomain = z.infer<typeof EmailDomainSchema>;

export function parseEmailDomain(data: unknown) {
  const parseResult = EmailDomainSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'emailDomain');
    throw new SilentError();
  }

  return parseResult.data;
}

export async function loadEmailDomainQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(EMAIL_DOMAIN_URL);

  return parseEmailDomain(data);
}

export function useLoadEmailDomain(options: QueryOptions<EmailDomain> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<EmailDomain, ApiError>({
    queryKey: EMAIL_DOMAIN_QUERY_KEY,
    queryFn: () => loadEmailDomainQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<EmailDomain, ApiError, 'emailDomain'>(
    'emailDomain',
    queryResult,
  );
}
